import { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { X, ChevronRight } from 'lucide-react';

interface TutorialTooltipProps {
  id: string;
  title: string;
  description: string;
  children: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

const STORAGE_KEY = 'odyvoy_tutorial_completed';

export function TutorialTooltip({ 
  id, 
  title, 
  description, 
  children,
  placement = 'bottom'
}: TutorialTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenShown, setHasBeenShown] = useState(false);

  useEffect(() => {
    const completedTutorials = getCompletedTutorials();
    if (!completedTutorials.includes(id)) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setHasBeenShown(true);
    }
  }, [id]);

  const getCompletedTutorials = (): string[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const markAsCompleted = () => {
    try {
      const completed = getCompletedTutorials();
      if (!completed.includes(id)) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...completed, id]));
      }
      setIsOpen(false);
      setHasBeenShown(true);
    } catch (error) {
      console.error('Error saving tutorial progress:', error);
    }
  };

  if (hasBeenShown) {
    return <>{children}</>;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent 
        side={placement} 
        className="w-80 bg-primary text-primary-foreground border-primary shadow-lg"
      >
        <div className="space-y-3">
          <div className="flex items-start justify-between">
            <h4 className="font-semibold text-sm">{title}</h4>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-primary-foreground hover:bg-primary-foreground/20"
              onClick={markAsCompleted}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-primary-foreground/90">{description}</p>
          <Button
            size="sm"
            variant="secondary"
            className="w-full"
            onClick={markAsCompleted}
          >
            Got it <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
